import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { TextField, type TextFieldProps } from "../TextField";
import { DropdownMenu, type DropdownMenuItem } from "../Dropdown/DropdownMenu";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";
import type { SelectOption } from "../Select";
import { BottomSheet } from "../BottomSheet";
import { Icon } from "../../lib";

export interface SelectTextFieldProps
  extends Omit<
    TextFieldProps,
    "suffix" | "onChange" | "value" | "defaultValue"
  > {
  /**
   * Text input value (controlled)
   */
  textValue?: string;
  /**
   * Default text input value (uncontrolled)
   */
  defaultTextValue?: string;
  /**
   * Callback when text input changes
   */
  onTextChange?: (value: string) => void;
  /**
   * Options for the select dropdown
   */
  selectOptions?: SelectOption[];
  /**
   * Selected value for the select (controlled)
   */
  selectValue?: string | number;
  /**
   * Default selected value for the select (uncontrolled)
   */
  defaultSelectValue?: string | number;
  /**
   * Callback when select value changes
   */
  onSelectChange?: (value: string | number, option: SelectOption) => void;
  /**
   * Placeholder for the select dropdown
   */
  selectPlaceholder?: string;
  /**
   * Custom class for the select trigger
   */
  selectTriggerClassName?: string;
  /**
   * Custom class for the select dropdown menu
   */
  selectMenuClassName?: string;
  /**
   * Width of the select dropdown menu
   */
  selectMenuWidth?: "auto" | "full" | string;
  /**
   * Section heading for the select dropdown
   */
  selectSectionHeading?: string;
  /**
   * Empty state title for select
   */
  selectEmptyTitle?: string;
  /**
   * Empty state description for select
   */
  selectEmptyDescription?: string;
  /**
   * Empty state icon for select
   */
  selectEmptyIcon?: React.ReactNode;
  /**
   * Position of the select field relative to the text input
   * @default "suffix"
   */
  position?: "prefix" | "suffix";
  /**
   * Whether the select dropdown is disabled (independent of text field)
   * When true, only the select portion is disabled. Use `isDisabled` to disable both.
   * @default false
   */
  isSelectDisabled?: boolean;
  /**
   * Whether the text field is disabled (independent of select)
   * When true, only the text field portion is disabled. Use `isDisabled` to disable both.
   * @default false
   */
  isTextFieldDisabled?: boolean;
}

const selectTriggerVariants = cva(
  "flex items-center gap-2 transition-all font-functional font-size-100 leading-100",
  {
    variants: {
      size: {
        small: "px-2 text-xs",
        medium: "px-2 text-sm",
        large: "px-3 text-base",
      },
      validationState: {
        none: "",
        positive: "",
        negative: "",
      },
      isDisabled: {
        true: "opacity-60 cursor-not-allowed",
        false: "cursor-pointer hover:opacity-80",
      },
    },
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isDisabled: false,
    },
  }
);

export const SelectTextField = React.forwardRef<
  HTMLInputElement,
  SelectTextFieldProps
>(
  (
    {
      textValue: controlledTextValue,
      defaultTextValue,
      onTextChange,
      selectOptions = [],
      selectValue: controlledSelectValue,
      defaultSelectValue,
      onSelectChange,
      selectPlaceholder = "Select",
      selectTriggerClassName,
      selectMenuClassName,
      selectMenuWidth = "auto",
      selectSectionHeading,
      selectEmptyTitle = "No options available",
      selectEmptyDescription = "There are no options to select from.",
      selectEmptyIcon,
      position = "suffix",
      isSelectDisabled = false,
      isTextFieldDisabled = false,
      label,
      helperText,
      errorText,
      successText,
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      size = "medium",
      containerClassName,
      labelClassName,
      inputClassName,
      className,
      ...textFieldProps
    },
    ref
  ) => {
    const valuesMatch = React.useCallback(
      (
        left: string | number | undefined,
        right: string | number | undefined
      ): boolean => {
        if (left === right) return true;
        if (left === undefined || right === undefined) return false;
        return String(left) === String(right);
      },
      []
    );

    const [uncontrolledTextValue, setUncontrolledTextValue] = React.useState(
      defaultTextValue || ""
    );
    const [uncontrolledSelectValue, setUncontrolledSelectValue] =
      React.useState<string | number | undefined>(defaultSelectValue);
    const [isSelectOpen, setIsSelectOpen] = React.useState(false);
    const [dropdownPlacement, setDropdownPlacement] = React.useState<
      "top" | "bottom"
    >("bottom");
    const [isMobile, setIsMobile] = React.useState(false);
    const selectRef = React.useRef<HTMLDivElement>(null);
    const dropdownContainerRef = React.useRef<HTMLDivElement>(null);
    const componentRef = React.useRef<HTMLDivElement>(null);

    // Detect if we're on mobile (< 768px)
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      // Check on mount
      checkMobile();

      // Add resize listener
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const textValue =
      controlledTextValue !== undefined
        ? controlledTextValue
        : uncontrolledTextValue;
    const selectValue =
      controlledSelectValue !== undefined
        ? controlledSelectValue
        : uncontrolledSelectValue;

    // Compute effective disabled states
    // isDisabled disables both; individual props allow disabling each independently
    const effectiveSelectDisabled = isDisabled || isSelectDisabled;
    const effectiveTextFieldDisabled = isDisabled || isTextFieldDisabled;

    // Find the selected option (supports number/string equivalence like 1.14 vs "1.14")
    const selectedOption = selectOptions.find((opt) =>
      valuesMatch(opt.value as string | number | undefined, selectValue)
    );

    // Keep uncontrolled default in sync when defaultSelectValue changes
    React.useEffect(() => {
      if (controlledSelectValue === undefined) {
        setUncontrolledSelectValue(defaultSelectValue);
      }
    }, [controlledSelectValue, defaultSelectValue]);

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText;
    const currentValidationState = errorText
      ? "negative"
      : successText
        ? "positive"
        : validationState;

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (onTextChange) {
        onTextChange(newValue);
      } else {
        setUncontrolledTextValue(newValue);
      }
    };

    const handleSelectOpenChange = (newOpen: boolean) => {
      if (!effectiveSelectDisabled) {
        setIsSelectOpen(newOpen);
      }
    };

    const toggleSelectOpen = () => {
      handleSelectOpenChange(!isSelectOpen);
    };

    const handleSelect = (option: SelectOption) => {
      if (controlledSelectValue === undefined) {
        setUncontrolledSelectValue(option.value);
      }
      onSelectChange?.(option.value, option);
      setIsSelectOpen(false);
    };

    const updateDropdownPlacement = React.useCallback(() => {
      if (typeof window === "undefined") return;
      const trigger = selectRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      const dropdownHeight = dropdownContainerRef.current
        ? dropdownContainerRef.current.offsetHeight
        : 0;

      if (dropdownHeight === 0) {
        setDropdownPlacement(spaceBelow >= spaceAbove ? "bottom" : "top");
        return;
      }

      if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
        setDropdownPlacement("bottom");
      } else {
        setDropdownPlacement("top");
      }
    }, []);

    const attachDropdownListeners = React.useCallback(() => {
      if (!isSelectOpen) return;
      if (typeof window === "undefined") return;
      let rafId = requestAnimationFrame(updateDropdownPlacement);

      const handleUpdate = () => updateDropdownPlacement();
      window.addEventListener("resize", handleUpdate);
      window.addEventListener("scroll", handleUpdate, true);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", handleUpdate);
        window.removeEventListener("scroll", handleUpdate, true);
      };
    }, [isSelectOpen, updateDropdownPlacement]);

    React.useEffect(() => {
      const detach = attachDropdownListeners();
      return () => {
        detach?.();
      };
    }, [attachDropdownListeners]);

    React.useEffect(() => {
      if (isSelectOpen) {
        updateDropdownPlacement();
      }
    }, [isSelectOpen, selectOptions.length, updateDropdownPlacement]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          selectRef.current &&
          !selectRef.current.contains(target) &&
          dropdownContainerRef.current &&
          !dropdownContainerRef.current.contains(target)
        ) {
          handleSelectOpenChange(false);
        }
      };

      if (isSelectOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isSelectOpen]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleSelectOpenChange(false);
        }
      };

      if (isSelectOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isSelectOpen]);

    // Transform options to dropdown menu items
    const menuItems: DropdownMenuItem[] = selectOptions.map((option) => ({
      value: option.value,
      label: option.label ?? String(option.value),
      description: option.description,
      leadingIcon: option.leadingIcon,
      trailingIcon: option.trailingIcon,
      isDisabled: option.isDisabled,
      variant: option.variant,
      onClick: () => handleSelect(option),
    }));

    const widthStyle =
      selectMenuWidth === "full"
        ? "100%"
        : selectMenuWidth === "auto"
          ? "auto"
          : selectMenuWidth;

    const sizeConfig = {
      small: {
        gap: "gap-2",
      },
      medium: {
        gap: "gap-2",
      },
      large: {
        gap: "gap-3",
      },
    };

    // Render dropdown menu content
    const renderDropdownContent = () => (
      <DropdownMenu
        items={menuItems}
        sectionHeading={selectSectionHeading}
        isEmpty={selectOptions.length === 0}
        emptyTitle={selectEmptyTitle}
        emptyDescription={selectEmptyDescription}
        emptyIcon={selectEmptyIcon}
        disableFooter={true}
        onClose={() => handleSelectOpenChange(false)}
        className={selectMenuClassName}
        width={isMobile ? "full" : widthStyle}
        unstyled={isMobile}
      />
    );

    // Create the select component (prefix or suffix)
    const selectComponent = (
      <div className={cn("relative flex items-stretch h-full")}>
        <div
          ref={selectRef}
          className={cn(
            selectTriggerVariants({
              size,
              validationState: currentValidationState,
              isDisabled: effectiveSelectDisabled,
            }),
            "h-full flex items-center self-stretch",
            position === "prefix"
              ? "border-r border-action-outline-neutral-faded"
              : "border-l border-action-outline-neutral-faded",
            selectTriggerClassName,
            position === "prefix" ? "pr-4" : "pl-4"
          )}
          onClick={!effectiveSelectDisabled ? toggleSelectOpen : undefined}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isSelectOpen}
          aria-disabled={effectiveSelectDisabled}
        >
          <span
            className={cn(
              "text-left truncate max-w-[120px] whitespace-nowrap",
              !selectedOption && "text-surface-ink-neutral-muted",
              effectiveSelectDisabled && "text-surface-ink-neutral-disabled",
              selectedOption && 'text-surface-ink-neutral-normal'
            )}
          >
            {selectedOption?.label || selectPlaceholder}
          </span>

          <Icon name='chevronDown'
            className={cn(
              "shrink-0 transition-transform mt-1",
              size === "small"
                ? "w-3 h-3"
                : size === "medium"
                  ? "w-3.5 h-3.5"
                  : "w-4 h-4",
              effectiveSelectDisabled
                ? "text-surface-ink-neutral-disabled"
                : currentValidationState === "positive"
                  ? "text-feedback-ink-positive-intense"
                  : currentValidationState === "negative"
                    ? "text-feedback-ink-negative-subtle"
                    : "text-surface-ink-neutral-muted",
              isSelectOpen && "transform rotate-180"
            )}

          />
        </div>

        {/* Mobile: BottomSheet, Desktop: Regular Dropdown */}
        {isSelectOpen && !effectiveSelectDisabled && (
          isMobile ? (
            <BottomSheet
              isOpen={isSelectOpen}
              onClose={() => handleSelectOpenChange(false)}
              title={selectSectionHeading || label}
              variant="default"
              showDragHandle={true}
              closeOnOverlayClick={true}
              closeOnEscape={true}
              closeOnSwipeDown={true}
            >
              {renderDropdownContent()}
            </BottomSheet>
          ) : (
            <div
              ref={dropdownContainerRef}
              className={cn(
                "absolute z-50",
                position === "prefix" ? "left-[-12px]" : "right-[-12px]",
                dropdownPlacement === "bottom"
                  ? "top-[30px] mt-1"
                  : "bottom-full mb-1"
              )}
            >
              {renderDropdownContent()}
            </div>
          )
        )}
      </div>
    );

    return (
      <div
        ref={componentRef}
        className={cn(
          "w-full flex flex-col",
          sizeConfig[size].gap,
          containerClassName
        )}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={textFieldProps.infoHeading}
            infoDescription={textFieldProps.infoDescription}
            LinkComponent={textFieldProps.LinkComponent}
            linkText={textFieldProps.linkText}
            linkHref={textFieldProps.linkHref}
            onLinkClick={textFieldProps.onLinkClick}
            htmlFor={textFieldProps.id}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <TextField
          ref={ref}
          value={textValue}
          onChange={handleTextChange}
          {...(position === "prefix"
            ? { prefix: selectComponent }
            : { suffix: selectComponent })}
          size={size}
          validationState={currentValidationState}
          isDisabled={effectiveTextFieldDisabled}
          isRequired={isRequired}
          isOptional={isOptional}
          containerClassName="gap-0"
          className={className}
          inputClassName={inputClassName}
          {...textFieldProps}
        />

        <FormFooter
          helperText={displayHelperText}
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState
          }
          size={size}
          isDisabled={effectiveSelectDisabled && effectiveTextFieldDisabled}
          className="mt-1"
        />
      </div>
    );
  }
);

SelectTextField.displayName = "SelectTextField";

export { selectTriggerVariants };
